import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { ProjectConversion } from ".";
import { ButtonBase, ButtonIconTrash, InputSwitcher } from "../../../common/components/form";
import { useGetPaymentStatus, useReduxAccount } from "../../hooks";
import * as API from "../../lib/api.js";
import * as Links from "../../lib/links.js";
import ms2hr from "../../lib/ms2hr.js";
import Confirm from "../Confirm";
import Tooltip from "../Tooltip";
import WebsiteMarketingModal from "../Website/WebsiteMarketing/WebsiteMarketingModal.jsx";

let LoopTimeoutList = [];
const AddLoopTimeout = (callback, ms) => {
    const tid = setTimeout(callback, ms);
    LoopTimeoutList.push(tid);
    return tid;
};
const DelLoopTimeout = timeoutId => {
    timeoutId !== "*" && clearTimeout(timeoutId);
    LoopTimeoutList = LoopTimeoutList.filter(_timeoutId => {
        if (timeoutId === "*") {
            clearTimeout(_timeoutId);
            return false;
        }
        return timeoutId !== _timeoutId;
    });
};

export default function ProjectItem(props) {
    if (!props.allLoaded) {
        return <div className="projects-item">...</div>;
    }

    const {
        data,
        data: { id: id_project, domain, variables, active },
        setWait,
        setLoading,
        loadProjectList,
        canConversion,
        GTC,
    } = props;

    const canDelete = !(domain === "pgbldr.com" || domain === "dev.pgbldr.com");

    const isECommerce = variables?.ProductType === "ecommerce";

    const projectLocalisation = variables?.Localisation;

    const { t } = useTranslation();
    const history = useHistory();
    const { free, needToPay } = useGetPaymentStatus();
    const { marketing } = useReduxAccount();

    const [projectStats, setProjectStats] = React.useState({});
    const [ConfirmDelete, setConfirmDelete] = React.useState({ show: false });

    const [showConversion, setShowAdditionals] = React.useState({ show: false });
    const [conversionStatus, setConversionStatus] = React.useState(null);
    const [conversionExperimentMessage, setConversionExperimentMessage] = React.useState({ show: false });
    const [marketingModal, setMarketingModal] = React.useState({ id_project, show: false });

    const refRoot = React.useRef();

    React.useEffect(() => {
        const domRoot = refRoot.current;
        const isActive = active !== false && variables?.visibility !== false;

        // Load + Loop
        // let isIntersecting = false;
        let looping = false;
        let loopTimeoutId;
        async function loop() {
            await loadProjectStats();

            if (loopTimeoutId) {
                DelLoopTimeout(loopTimeoutId);
                loopTimeoutId = null;
            }

            if (isActive) {
                loopTimeoutId = AddLoopTimeout(loop, 30000);
            }
        }
        loop();

        // Pause loop loading by intersection
        const margin = innerHeight / 2;

        const observer = new IntersectionObserver(
            entries => {
                for (let entry of entries) {
                    if (looping === entry.isIntersecting) {
                        continue;
                    }

                    looping = entry.isIntersecting;

                    domRoot.classList[looping ? "add" : "remove"]("looping");

                    if (looping && isActive && !loopTimeoutId) {
                        loop();
                    } else if (!looping && (!isActive || loopTimeoutId)) {
                        DelLoopTimeout(loopTimeoutId);
                        loopTimeoutId = null;
                    }
                }
            },
            { rootMargin: `${margin}px 0px ${margin}px 0px` }
        );

        observer.observe(domRoot);

        // Conversion Status
        loadConversionStatus();

        // UnMount
        return () => {
            DelLoopTimeout(loopTimeoutId);
        };
    }, [id_project]);

    React.useEffect(() => {
        return () => {
            DelLoopTimeout("*");
        };
    }, []);

    function loadProjectStats() {
        return API.ProjectStats(id_project).then(res => {
            if (!res) {
                return;
            }

            setProjectStats({ ...res });
        });
    }

    function loadConversionStatus() {
        return API.ProjectConversionStatus(domain)
            .then(res => setConversionStatus(res?.status !== "active" ? "inactive" : "active"))
            .catch(e => console.warn(">>>", e));
    }

    function onVisibilityChange(checked) {
        setWait(true);
        API.ProjectUpdate({
            id_project,
            ...data,
            variables: { ...variables, visibility: checked },
        })
            .then(res => {
                if (!res?.ok) {
                    console.warn(res);
                    return;
                }

                return API.CompileRender(domain);
            })
            .then(() => loadProjectList())
            .finally(() => setWait(false));
    }

    function onEditAnalyticsClick(id) {
        history.push(Links.WebsiteEditAnalytics(id));
    }

    function onEditMarketingClick(id) {
        history.push(Links.WebsiteEditMarketing(id));
    }

    function onEditDomainClick(id) {
        history.push(Links.WebsiteEditDomain(id));
    }

    function onEditWebsiteClick(id) {
        if (conversionStatus !== "active") {
            history.push(Links.WebsiteEditDesign(id));
        } else {
            setConversionExperimentMessage({ ...conversionExperimentMessage, show: true });
        }
    }

    function onEditECommerceClick(id) {
        history.push(Links.WebsiteEditECommerce(id));
    }

    function onDeleteClick(project, ctrlKey) {
        if (ctrlKey) {
            return deleteProjectById(id_project, domain);
        }

        setConfirmDelete({ show: true, project });
    }

    function onDeleteCancel() {
        setConfirmDelete({ ...ConfirmDelete, show: false });
    }

    function onDeleteConfirm() {
        const { id, domain } = ConfirmDelete.project;
        return deleteProjectById(id, domain);
    }

    function onShowConversionClick() {
        setShowAdditionals({ ...showConversion, show: !showConversion?.show });
    }

    function onConversionChangeStart() {
        setWait(true);
    }

    function onConversionChangeEnd() {
        setWait(true);
        Promise.all([loadProjectList(), loadConversionStatus()]).finally(() => setWait(false));
    }

    function onCloseConversionExperimentMessage() {
        setConversionExperimentMessage({ ...conversionExperimentMessage, show: false });
    }

    function deleteProjectById(id, domain) {
        setLoading(true);
        API.ProjectDelete(id)
            .then(res => {
                if (res?.errors) {
                    console.warn(res?.errors);
                    return;
                }

                if (!res?.ok) {
                    console.warn(`Unknown error while project deleting.`);
                    return;
                }

                return API.CompileDelete(domain);
            })
            .then(loadProjectList)
            .finally(() => {
                setConfirmDelete({ ...ConfirmDelete, show: false });
                setLoading(false);
            });
    }

    /**
     * Float to Human readable
     */
    function f2hr(float = 0, suffix = "") {
        if (float === null || float === undefined) {
            return "...";
        } else if (isNaN(float)) {
            return `0`;
        }

        return `${Math.round(float * 1000) / 1000}${suffix}`;
    }

    /**
     *
     * Number format '12345678.112233' -> '12 345 678.112233'
     */
    function numfmt(number = 0) {
        const [integer, decimal] = number.toString().split(/[\.\,]/);

        if (!integer && !decimal) {
            return number;
        }

        return [
            integer
                .split("")
                .reverse()
                .join("")
                .replaceAll(/(\d{3})/g, "$1 ")
                .split("")
                .reverse()
                .join(""),

            decimal,
        ]
            .filter(v => !!v)
            .join(".");
    }

    const visibility = variables?.visibility !== false && active;
    return (
        <>
            <div ref={refRoot} className="projects-item">
                {/* Desktop Project Top */}
                <div className="projects-item-top desktop">
                    <div className="input-switcher-container">
                        <Tooltip
                            text={visibility ? t("YourWebsiteIsVisible") : t("YourWebsiteIsNOTVisible")}
                            disabled={!active}
                        >
                            <InputSwitcher
                                onChange={(value, checked) => onVisibilityChange(checked)}
                                checked={visibility}
                                disabled={!active}
                            />
                        </Tooltip>
                    </div>

                    <div className="projects-item-title">
                        <a href={`//${domain}`} target="_blank">
                            {domain}
                        </a>

                        <p className="projects-item-localisation">
                            {projectLocalisation && projectLocalisation === "uk" ? "/ua" : `/${projectLocalisation}`}
                        </p>
                    </div>
                </div>

                {/* Mobile Project Top */}
                <div className="projects-item-top mobile">
                    <div className="input-switcher-container">
                        <InputSwitcher
                            onChange={(value, checked) => onVisibilityChange(checked)}
                            checked={visibility}
                            disabled={!active}
                        />
                    </div>

                    <div className="grid-column">
                        <div>
                            <ButtonIconTrash
                                onClick={event => onDeleteClick(data, event.metaKey || event.ctrlKey)}
                                disabled={!canDelete}
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile Project Title */}
                <div className="projects-item-title mobile">
                    <a href={`//${domain}`} target="_blank">
                        {domain}
                    </a>

                    <p className="projects-item-localisation">
                        {projectLocalisation && projectLocalisation === "uk" ? "/ua" : `/${projectLocalisation}`}
                    </p>
                </div>

                {/* Desktop Delete Button */}
                <div className="projects-item-delete-container desktop">
                    <div className="projects-item-delete absolute">
                        <Tooltip text={canDelete && t("DeleteWebsite")}>
                            <ButtonIconTrash
                                onClick={event => onDeleteClick(data, event.metaKey || event.ctrlKey)}
                                disabled={!canDelete}
                            />
                        </Tooltip>
                    </div>
                </div>

                <div className="projects-item-stats">
                    <div>
                        <div className="projects-item-stat">
                            {t("ProjectOverallSessions")}: <b>{projectStats.sessions ?? "..."}</b>
                        </div>

                        <div className="projects-item-stat">
                            {t("ProjectOverallSessionsWithClicks")}: <b>{projectStats.sessionsWithClicks ?? "..."}</b>
                        </div>

                        <div className="projects-item-stat">
                            {t("ProjectOverallViews")}: <b>{projectStats.visits ?? "..."}</b>
                        </div>

                        {projectStats.clicksSeeDemo > 0 && (
                            <div className="projects-item-stat">
                                {t("ProjectSeeDemoClicks")}: <b>{projectStats.clicksSeeDemo ?? "..."}</b>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="projects-item-stat">
                            {t("ProjectAverageTimeSpent")}: <b>{ms2hr(projectStats.averageTimeSpent ?? "...")}</b>
                        </div>

                        <div className="projects-item-stat">
                            {t("ProjectOverallClicks")}: <b>{projectStats.clicks ?? "..."}</b>
                        </div>

                        <div className="projects-item-stat">
                            {t("ProjectOverallConversionRate")}:{" "}
                            <b>{f2hr(projectStats.conversionRate ?? "...", "%")}</b>
                        </div>
                    </div>

                    {variables.ProductType === "ecommerce" && (
                        <div>
                            <div className="projects-item-stat">
                                {t("ProjectECommerceClicks")}: <b>{numfmt(projectStats.clicksCheckout ?? "...")}</b>
                            </div>

                            <div className="projects-item-stat">
                                {t("ProjectECommerceCTR")}:{" "}
                                <b>{f2hr(projectStats.conversionRateCheckout ?? "...", "%")}</b>
                            </div>

                            <div className="projects-item-stat">
                                {t("ProjectECommerceSales")}: <b>{numfmt(projectStats.sales ?? "...")}</b>
                            </div>

                            <div className="projects-item-stat">
                                {t("ProjectECommerceEarnings")}:{" "}
                                <b>
                                    {(() => {
                                        const last = projectStats.earnings?.[0];
                                        const n = numfmt(last?.value ?? "...");
                                        const c = last?.currency;

                                        return [n, c].filter(v => !!v).join(" ");
                                    })()}
                                </b>
                            </div>
                        </div>
                    )}
                </div>

                <div className="projects-item-actions">
                    <div className="col-3">
                        <ButtonBase
                            solid
                            disabled={needToPay && !free}
                            onClick={() => onEditAnalyticsClick(id_project)}
                        >
                            {t("EditAnalytics")}
                        </ButtonBase>

                        <ButtonBase solid disabled={needToPay && !free} onClick={() => onEditDomainClick(id_project)}>
                            {t("EditDomain")}
                        </ButtonBase>

                        <ButtonBase solid onClick={() => onEditWebsiteClick(id_project)}>
                            {t("EditProductPage")}
                        </ButtonBase>
                    </div>

                    <div className={`col-${!!marketing + !!canConversion + !!isECommerce}`}>
                        {marketing && (
                            <ButtonBase invert onClick={() => onEditMarketingClick(id_project)}>
                                {t("EditMarketing")}
                            </ButtonBase>
                        )}

                        {canConversion && (
                            <ButtonBase invert onClick={() => onShowConversionClick()}>
                                {t("EditConversion")}
                            </ButtonBase>
                        )}

                        {isECommerce && (
                            <ButtonBase invert onClick={() => onEditECommerceClick(id_project)}>
                                {/* {t("EditECommerce")} */}
                                {t("ECommerce")}
                            </ButtonBase>
                        )}
                    </div>
                </div>

                <div className="projects-item-hint-marketing">
                    Run your{" "}
                    <ButtonBase
                        link
                        onClick={() => setMarketingModal({ ...marketingModal, show: true })}
                        style={{ color: "#000000" }}
                    >
                        <b>Marketing Campaign</b>
                    </ButtonBase>{" "}
                    to build-up your conversion rates!
                </div>

                <WebsiteMarketingModal
                    id={marketingModal?.id_project}
                    show={marketingModal?.show}
                    data={variables?.GoogleAds}
                    GTC={GTC}
                    onModalClose={() => setMarketingModal({ ...marketingModal, show: false })}
                />

                {/* Conversion --------------------------------------------- */}
                {canConversion && (
                    <ProjectConversion
                        show={showConversion.show}
                        onShowConversionClick={() => onShowConversionClick()}
                        onChangeStart={() => onConversionChangeStart()}
                        onChangeEnd={() => onConversionChangeEnd()}
                        data={data}
                    />
                )}
                {/* -------------------------------------------------------- */}
            </div>

            <Confirm
                show={ConfirmDelete.show}
                header={t("DeletingTheWebsite").replace("%site%", ConfirmDelete?.project?.domain)}
                text={t("DeletingTheWebsiteMessage")}
                actions={[
                    { onClick: () => onDeleteCancel(), text: t("Cancel") },
                    {
                        onClick: () => onDeleteConfirm(ConfirmDelete.project),
                        text: t("Confirm"),
                        props: { invert: true },
                    },
                ]}
            />

            <Confirm
                show={conversionExperimentMessage.show}
                text={t("ConversionExperimentMessage")}
                actions={[
                    {
                        onClick: () => onCloseConversionExperimentMessage(),
                        text: t("OK"),
                        props: { invert: true },
                    },
                ]}
            />
        </>
    );
}
